import { Dialog } from '@base-ui/react/dialog';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { DIMENSION_RUBRICS, formatDimension, parseRubricLevels, parseRubricPreamble } from '../lib/rubrics';
import { levelLabel, levelBadgeClass } from '../lib/score';
import './DimensionRubricDialog.css';

interface DimensionRubricDialogProps {
  /** Ordered dimension keys as rendered in the grid (drives prev/next). */
  dimensions: string[];
  /** Currently open dimension key, or null when the dialog is closed. */
  activeDimension: string | null;
  /** Phase word for the title, e.g. "Delivery" or "Discovery". */
  phaseLabel?: string;
  /** Close the dialog. */
  onClose: () => void;
  /** Point the dialog at another dimension without closing it. */
  onNavigate: (dimension: string) => void;
}

/**
 * Shared modal that explains the L1–L5 rubric for a dimension. Opening it floats
 * the rubric over the grid (Base UI Dialog → portal) so the dimension cards never
 * reflow — replacing the old inline expansion that grew the card and shoved the
 * grid. Prev/next steps through every dimension without closing (matches the
 * "Delivery Dimensions Explained" Figma exploration, V1).
 *
 * The modal is pinned to the top of the viewport (not vertically centred) so the
 * header and ‹ › nav arrows stay put as you navigate — different dimensions wrap
 * to different heights, but only the content below the arrows grows/shrinks, so
 * the cursor stays over the arrow it just clicked.
 */
export function DimensionRubricDialog({
  dimensions,
  activeDimension,
  phaseLabel,
  onClose,
  onNavigate,
}: DimensionRubricDialogProps) {
  const open = activeDimension !== null;
  const index = activeDimension ? dimensions.indexOf(activeDimension) : -1;
  const total = dimensions.length;
  const rubric = activeDimension ? DIMENSION_RUBRICS[activeDimension] : undefined;
  // Levels ordered high → low (L5 first) per the chosen design.
  const levels = rubric ? parseRubricLevels(rubric).reverse() : [];
  const preamble = rubric ? parseRubricPreamble(rubric) : [];
  const canNavigate = total > 1 && index >= 0;

  const step = (delta: number) => {
    if (index < 0 || total === 0) return;
    const nextIndex = (index + delta + total) % total;
    const target = dimensions[nextIndex];
    if (target) onNavigate(target);
  };

  const title = phaseLabel ? `${phaseLabel} Dimensions Explained` : 'Dimensions Explained';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="rubric-dialog-backdrop" />
        <Dialog.Popup
          className="rubric-dialog"
          data-testid="dimension-rubric-dialog"
          onKeyDown={(e) => {
            if (!canNavigate) return;
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              step(-1);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              step(1);
            }
          }}
        >
          <div className="rubric-dialog__head">
            <Dialog.Title className="rubric-dialog__title">{title}</Dialog.Title>
            <Dialog.Close className="rubric-dialog__close" aria-label="Close">
              <XMarkIcon aria-hidden="true" width={20} height={20} />
            </Dialog.Close>
          </div>

          <div className="rubric-dialog__nav" data-testid="rubric-dialog-nav">
            <Button
              variant="secondary"
              size="S"
              onClick={() => step(-1)}
              disabled={!canNavigate}
              aria-label="Previous dimension"
              leftIcon={<ChevronLeftIcon />}
            />
            <div className="rubric-dialog__nav-label">
              <span className="rubric-dialog__dim-name">
                {activeDimension ? formatDimension(activeDimension) : ''}
              </span>
              {index >= 0 && total > 0 && (
                <span className="rubric-dialog__counter">
                  {' '}
                  · {index + 1} of {total}
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="S"
              onClick={() => step(1)}
              disabled={!canNavigate}
              aria-label="Next dimension"
              leftIcon={<ChevronRightIcon />}
            />
          </div>

          {preamble.length > 0 && (
            <div className="rubric-dialog__preamble" data-testid="rubric-dialog-preamble">
              {preamble.map((line, i) => (
                <p key={i} className="rubric-dialog__preamble-line">
                  {line}
                </p>
              ))}
            </div>
          )}

          <ul className="rubric-dialog__levels" data-testid="dimension-rubric">
            {levels.map(({ level, text }) => (
              <li key={level} className="rubric-dialog__level">
                <span className={levelBadgeClass(level)}>
                  {levelLabel(level)}
                </span>
                <p className="rubric-dialog__level-text">{text}</p>
              </li>
            ))}
          </ul>

          {canNavigate && (
            <p className="rubric-dialog__foot">Use ‹ › (or ← →) to move between dimensions.</p>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
